import { useEffect, useState } from "react";
import { listAgendas, placeNote, type AgendaChoice } from "./agenda";
import { createTask, listProperties, type TaskProperty } from "./tasks";
import { FicheMark } from "./FicheMark";
import { Modal } from "./Modal";
import { ChoiceField, DateField, TimeField } from "./pickers";
import { saveSuivi } from "./suivi";

export type NoteOrigin = { id: number; name: string; logo: string };

type Place = "agenda" | "kanban";

export function PlaceSheet({ note, origin, initialDay = "", onClose }: { note: string; origin?: NoteOrigin; initialDay?: string; onClose: () => void }) {
  const [place, setPlace] = useState<Place>("agenda");
  const [day, setDay] = useState(validDay(initialDay) || today());
  const [time, setTime] = useState("09:00");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [agendas, setAgendas] = useState<AgendaChoice[]>([]);
  const [calendarId, setCalendarId] = useState("");
  const [properties, setProperties] = useState<TaskProperty[]>([]);
  const [picked, setPicked] = useState<Record<string, string>>({});

  useEffect(() => {
    let alive = true;
    void listAgendas()
      .then((rows) => {
        if (!alive) return;
        setAgendas(rows);
        setCalendarId(rows[0]?.id || "");
      })
      .catch((reason: unknown) => {
        if (alive) setError(reason instanceof Error ? reason.message : "Agendas indisponibles.");
      });
    void listProperties()
      .then((rows) => {
        if (alive) setProperties(rows);
      })
      .catch(() => {
        if (alive) setProperties([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function confirm(close: () => void) {
    const title = note.split("\n").map((line) => line.trim()).find(Boolean) || "Note";
    setBusy(true);
    setError("");
    try {
      if (place === "agenda") await placeNote({ title, place, day, time, calendarId });
      else {
        await createTask({ title, day, time, allDay: false, fiche: origin ? { id: origin.id, name: origin.name } : undefined, props: Object.fromEntries(Object.entries(picked).filter(([, value]) => value)) });
        if (origin) {
          await saveSuivi(origin.id, {
            action: "sync",
            note_key: title.slice(0, 180),
            title: title.slice(0, 180),
            kind: "meeting",
            // Statut note / Kanban indépendants : ne pas forcer done ici.
            due_on: day,
          }).catch(() => undefined);
        }
      }
      setDone(place === "agenda" ? "Posée dans l’Agenda." : "Tâche créée dans Mes tâches.");
      window.setTimeout(close, 700);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Envoi impossible.");
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {(close) => (
        <>
          <h2 className="text-lg font-semibold">Placer la note</h2>
          <p className="mt-1 text-sm text-slate-500">Le texte reste dans le Coffre. Ici, seule la première ligne part.</p>
          {origin ? (
            <p className="mt-3 flex items-center gap-2 text-sm">
              <FicheMark path={origin.logo} name={origin.name} className="h-6 w-6 text-[11px]" />
              <span>Marquée <span className="font-semibold">{origin.name}</span></span>
            </p>
          ) : null}
          <div className="mt-4 flex gap-2">
            <Choice label="Agenda" on={place === "agenda"} onClick={() => { setPlace("agenda"); setDone(""); setError(""); }} />
            <Choice label="Mes tâches" on={place === "kanban"} onClick={() => { setPlace("kanban"); setDone(""); setError(""); }} />
          </div>
          {place === "agenda" ? (
            <label className="mt-4 block text-sm">
              <span className="mb-1 block text-xs font-medium text-slate-500">Agenda</span>
              <ChoiceField
                value={calendarId}
                placeholder={agendas.length === 0 ? "Aucun agenda" : "Choisir"}
                options={agendas.map((item) => ({ value: item.id, label: item.name }))}
                onChange={setCalendarId}
              />
            </label>
          ) : null}
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-slate-500">{place === "agenda" ? "Jour" : "Jour d’arrivée"}</span>
              <DateField value={day} onChange={setDay} />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-slate-500">Heure</span>
              <TimeField value={time} onChange={setTime} />
            </label>
          </div>
          {place === "kanban" && properties.length > 0 ? (
            <div className="mt-3 grid gap-3">
              {properties.map((prop) => {
                const color = prop.values.find((value) => value.key === picked[prop.key])?.color;
                return (
                  <label key={prop.key} className="block text-sm">
                    <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-slate-500">
                      {color ? <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} /> : null}
                      {prop.label}
                    </span>
                    <ChoiceField
                      value={picked[prop.key] || ""}
                      placeholder="—"
                      options={[{ value: "", label: "—" }, ...prop.values.map((value) => ({ value: value.key, label: value.label, color: value.color }))]}
                      onChange={(next) => setPicked({ ...picked, [prop.key]: next })}
                    />
                  </label>
                );
              })}
            </div>
          ) : null}
          <div className="mt-5 flex items-center justify-end gap-2">
            <button type="button" className="rounded-full crm-fill px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50" disabled={busy || (place === "agenda" && !calendarId)} onClick={() => void confirm(close)}>
              {busy ? "Envoi…" : place === "agenda" ? "Poser dans l’Agenda" : "Envoyer dans Mes tâches"}
            </button>
          </div>
          {error ? <p className="mt-3 text-sm text-rose-700">{error}</p> : null}
          {done ? <p className="mt-3 text-sm text-slate-600">{done}</p> : null}
        </>
      )}
    </Modal>
  );
}

function Choice({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button type="button" className={on ? "rounded-full crm-fill px-3 py-1.5 text-sm font-semibold text-white" : "crm-pill rounded-full px-3 py-1.5 text-sm font-semibold"} onClick={onClick}>
      {label}
    </button>
  );
}

function validDay(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
}

function today(): string {
  const date = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
